import {
  FlexContainer,
  IconLibrary,
  IconMessage,
  SingleCardPage,
  TransitionButton,
  ReemaThemeV2,
} from '@act/storybook';
import { useRouteError, useLocation } from 'react-router-dom';
import { CssBaseline, GlobalStyles } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import { useState, useEffect } from 'react';

const ErrorPage = () => {
  const error = useRouteError();
  const location = useLocation();
  const [fontsLoaded, setFontsLoaded] = useState(false);

  // If error is null, this is likely a 404 from the catch-all route
  const is404 = !error;

  // Wait for fonts to load before rendering to prevent font flash
  useEffect(() => {
    // Set a fallback timeout in case fonts don't load properly
    const fallbackTimeout = setTimeout(() => {
      setFontsLoaded(true);
    }, 1000);

    document.fonts.ready
      .then(() => {
        clearTimeout(fallbackTimeout);
        setFontsLoaded(true);
      })
      .catch(() => {
        // If fonts fail to load, render anyway
        clearTimeout(fallbackTimeout);
        setFontsLoaded(true);
      });

    return () => clearTimeout(fallbackTimeout);
  }, []);

  function handleCopy() {
    let errorText = 'Unknown error';

    if (is404) {
      errorText = `404 Not Found\n\nPath: ${location.pathname}${location.search}${location.hash}`;
    } else if (error instanceof Error) {
      errorText = `${error.name}: ${error.message}\n\nStack:\n${error.stack}`;
    } else if (typeof error === 'string') {
      errorText = error;
    } else if (error && typeof error === 'object') {
      try {
        errorText = JSON.stringify(error, null, 2);
      } catch {
        errorText = String(error);
      }
    }

    void navigator.clipboard.writeText(errorText);
  }

  // Don't render until fonts are loaded to prevent font flash
  if (!fontsLoaded) {
    return null;
  }

  return (
    <ThemeProvider theme={ReemaThemeV2.LIGHT_MODE}>
      <CssBaseline />
      <GlobalStyles
        styles={{
          '*': {
            fontFamily: "'Inclusive Sans', 'Lato', 'sans-serif' !important",
          },
        }}
      />
      <SingleCardPage showBorderTop={false}>
        <FlexContainer
          direction="column"
          alignItems="center"
          justifyContent="center"
          gap={3}
          sx={{ paddingBottom: 3 }}
        >
          <IconMessage
            message={is404 ? 'Page not found' : 'Unknown error'}
            subMessage={
              is404
                ? 'The page you are looking for does not exist. Click the copy button to copy the URL.'
                : 'Click the copy button and paste error message in #reema-app-help Slack channel with steps taken before the error occurred.'
            }
            iconName="WarningCircle"
            iconColor="error"
          />
          <TransitionButton
            transitionChildren="Error copied!"
            startIcon={
              <IconLibrary iconName="Copy" weight="regular" size={20} />
            }
            endIcon={
              <IconLibrary iconName="CheckCircle" weight="regular" size={20} />
            }
            onClick={handleCopy}
            duration={2000}
          >
            Copy error
          </TransitionButton>
        </FlexContainer>
      </SingleCardPage>
    </ThemeProvider>
  );
};

export default ErrorPage;
